import { createReducer, on } from '@ngrx/store';
import { produce } from 'immer';
import moment from 'moment';
import { Common } from 'src/app/lib/common-methods';
import { AuditTrail, Reservation, TablesSection } from 'src/app/model/reservation';
import * as ReservationActions from '../actions/reservation.actions';

export const reservationFeatureKey = 'reservation';

export interface State {
  reservation: Reservation | null | undefined;
  tablesAvailability: TablesSection[];
  reservationHistory: AuditTrail[];
  arrivalTime: { hours: number, mins: number } | null;
  readOnlyMode: boolean;
  isSubmiting: boolean;
  quickChangingStatus: boolean;

  paymentLinkExpiresAt: string | null;
  paymentLinkExpiresDirty: boolean;
}

export const initialState: State = {
  reservation: null,
  tablesAvailability: [],
  reservationHistory: [],
  arrivalTime: null,
  readOnlyMode: false,
  isSubmiting: false,
  quickChangingStatus: false,

  paymentLinkExpiresAt: null,
  paymentLinkExpiresDirty: false
};

export const reducer = createReducer(
  initialState,

  on(ReservationActions.setReservation, (state, action) => {
    return produce(state, (draftState) => {
      draftState.reservation = action.reservation;
      draftState.reservationHistory = [];
      
      // reset payment-link-expiration state for new reservation context
      draftState.paymentLinkExpiresAt = null;
      draftState.paymentLinkExpiresDirty = false;
    });
  }),
  on(ReservationActions.setQuickChangeStatusReservation, (state, action) => {
    return produce(state, (draftState) => {
      draftState.reservation = action.reservation;
      draftState.quickChangingStatus = true;
    });
  }),
  on(ReservationActions.setHideQuickChangeStatus, (state, action) => {
    return produce(state, (draftState) => {
      draftState.quickChangingStatus = false;
    });
  }),
  on(ReservationActions.loadTablesAvailabilitySuccess, (state, action) => {
    return produce(state, (draftState) => {
      draftState.tablesAvailability = action.tableSections;
    });
  }),
  on(ReservationActions.loadReservationHistorySuccess, (state, action) => {
    return produce(state, (draftState) => {
      draftState.reservationHistory = action.auditTrails;
    });
  }),
  on(ReservationActions.setReservationStatus, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.reservationsStatus = action.status;
      }
    });
  }),
  on(ReservationActions.setReservationPurpose, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.purpose = action.purpose;
      }
    });
  }),
  on(ReservationActions.setReservationArrivalDate, (state, action) => {
    if (state.reservation == null) {
      return state;
    }

    // do not affect time
    const arrivalTime = moment(state.reservation.reservationDetails.arrivalTime).toDate();

    return produce(state, (draftState) => {
      if (draftState.reservation != null) {

        draftState.reservation.reservationDetails.arrivalTime = moment(new Date(
          action.date.getFullYear(),
          action.date.getMonth(),
          action.date.getDate(),
          arrivalTime.getHours(),
          arrivalTime.getMinutes(),
          arrivalTime.getSeconds(),
          arrivalTime.getMilliseconds()
        )).format('yyyy-MM-DD HH:mm:ss.S');

        // adjust return time
        if (draftState.reservation.reservationDetails.returnTime != null && draftState.reservation.reservationDetails.returnTime.length > 0) {
          const returnTime = moment(draftState.reservation.reservationDetails.returnTime).toDate();
          const returnDate = moment(action.date).toDate();

          const isReturnTimeNextDayMorning = Common.timeIsNextDayMorning(returnTime);
          if (isReturnTimeNextDayMorning) {
            returnDate.setDate(returnDate.getDate() + 1);
          }

          draftState.reservation.reservationDetails.returnTime = moment(new Date(
            returnDate.getFullYear(),
            returnDate.getMonth(),
            returnDate.getDate(),
            returnTime.getHours(),
            returnTime.getMinutes(),
            returnTime.getSeconds(),
            returnTime.getMilliseconds()
          )).format('yyyy-MM-DD HH:mm:ss.S');
        }

      }
    });
  }),
  on(ReservationActions.setReservationArrivalTime, (state, action) => {
    if (state.reservation == null) {
      return state;
    }

    // do not affect date
    const arrivalTime = moment(state.reservation.reservationDetails.arrivalTime).toDate();

    const isCurrentTimeNextDayMorning = Common.timeIsNextDayMorning(arrivalTime) && state.arrivalTime != null;

    const updatedHours = action.hour;
    const isUpdatedTimeNextDayMorning = updatedHours < Common.WORKING_DAY_START_HOUR;

    let dayAdjustment = 0;
    if (isCurrentTimeNextDayMorning != isUpdatedTimeNextDayMorning) {
      dayAdjustment = isCurrentTimeNextDayMorning ? -1 : 1;
    }

    arrivalTime.setDate(arrivalTime.getDate() + dayAdjustment);

    return produce(state, (draftState) => {
      if (draftState.reservation != null) {

        draftState.reservation.reservationDetails.arrivalTime = moment(new Date(
          arrivalTime.getFullYear(),
          arrivalTime.getMonth(),
          arrivalTime.getDate(),
          action.hour,
          action.minute,
          arrivalTime.getSeconds(),
          arrivalTime.getMilliseconds()
        )).format('yyyy-MM-DD HH:mm:ss.S');

        draftState.arrivalTime = { hours: action.hour, mins: action.minute };
      }
    });
  }),
  on(ReservationActions.setReservationReturnTime, (state, action) => {
    if (state.reservation == null) {
      return state;
    }

    if (action.hour == null || action.minute == null) {
      return produce(state, (draftState) => {
        if (draftState.reservation != null) {
          draftState.reservation.reservationDetails.returnTime = '';
        }
      });
    }

    // do not affect date
    let momentReturnTime = moment(state.reservation.reservationDetails.returnTime);
    if (!momentReturnTime.isValid()) {
      momentReturnTime = moment(state.reservation.reservationDetails.arrivalTime);

      if (!momentReturnTime.isValid()) {
        momentReturnTime = moment();
      }
    }

    const returnTime = momentReturnTime.toDate();

    const isCurrentTimeNextDayMorning = Common.timeIsNextDayMorning(returnTime);

    const updatedHours = action.hour;
    const isUpdatedTimeNextDayMorning = updatedHours < Common.WORKING_DAY_START_HOUR;

    let dayAdjustment = 0;
    if (isCurrentTimeNextDayMorning != isUpdatedTimeNextDayMorning) {
      dayAdjustment = isCurrentTimeNextDayMorning ? -1 : 1;
    }

    returnTime.setDate(returnTime.getDate() + dayAdjustment);

    return produce(state, (draftState) => {
      if (draftState.reservation != null) {

        draftState.reservation.reservationDetails.returnTime = moment(new Date(
          returnTime.getFullYear(),
          returnTime.getMonth(),
          returnTime.getDate(),
          action.hour,
          action.minute,
          returnTime.getSeconds(),
          returnTime.getMilliseconds()
        )).format('yyyy-MM-DD HH:mm:ss.S');

      }
    });
  }),
  on(ReservationActions.setReservationCovers, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.covers = action.covers;
      }
    });
  }),
  on(ReservationActions.setReservationAllocatedTables, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.allocatedTables = action.allocatedTables.join(',');
      }
    });
  }),
  on(ReservationActions.setReservationTags, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.reservationTags = action.reservationTags;
      }
    });
  }),
  on(ReservationActions.setCustomerTags, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.customerDetails.customerTags = action.customerTags;
      }
    });
  }),
  on(ReservationActions.setReservationDietaryRestrictions, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.allergy = action.dietaryRestrictions;
      }
    });
  }),
  on(ReservationActions.setCustomerDietaryRestrictions, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.customerDetails.allergy = action.dietaryRestrictions;
      }
    });
  }),
  on(ReservationActions.setReservationDiscount, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.discount = action.discount;
      }
    });
  }),
  on(ReservationActions.setReservationBill, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.bill = action.bill;
      }
    });
  }),
  on(ReservationActions.setReservationExtraInfo, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.selectedMenu = action.selectedMenu;
        draftState.reservation.reservationDetails.extraInfo = action.extraInfo;
      }
    });
  }),
  on(ReservationActions.setReservationSmokingArea, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.smokingArea = action.value;
      }
    });
  }),
  on(ReservationActions.setReservationFixMenu, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.fixMenu = action.value;
      }
    });
  }),
  on(ReservationActions.setReservationHasCake, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.hasCake = action.value;
      }
    });
  }),
  on(ReservationActions.setOnlineCategory, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.onlineCategory = action.onlineCategory;
      }
    });
  }),
  on(ReservationActions.setReservationSource, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.sourceDetails = action.source;
      }
    });
  }),
  on(ReservationActions.setReservationNotes, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.notes = action.notes;
      }
    });
  }),
  on(ReservationActions.setReservationCustomer, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.customerDetails = action.customer;

        draftState.reservation.reservationDetails.reservationType = action.customer.customerType;

        // existing customer only
        if (action.customer.customerId != null && action.customer.customerId.trim() !== '' && action.customer.customerId !== '0') {
          // draftState.reservation.reservationDetails.allergy = action.customer.allergy; // I think there is no reason to do that after fully enabling tags
          draftState.reservation.reservationDetails.smokingArea = action.customer.smokingArea;
        }
      }
    });
  }),
  on(ReservationActions.setReservationCustomerType, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.customerDetails.customerType = action.customerType;
        draftState.reservation.reservationDetails.reservationType = action.customerType;
      }
    });
  }),
  on(ReservationActions.setReservationCustomerSurname, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.customerDetails.surname = action.surname;
      }
    });
  }),
  on(ReservationActions.setReservationCustomerName, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.customerDetails.name = action.name;
      }
    });
  }),
  on(ReservationActions.setReservationCustomerTelephone, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.customerDetails.telephone = action.telephone;
      }
    });
  }),
  on(ReservationActions.setReservationCustomerMail, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.customerDetails.mail = action.mail;
      }
    });
  }),
  on(ReservationActions.setArrivalTime, (state, action) => {
    return produce(state, (draftState) => {
      draftState.arrivalTime = action.arrivalTime;
    });
  }),
  on(ReservationActions.setReadOnlyMode, (state, action) => {
    return produce(state, (draftState) => {
      draftState.readOnlyMode = action.readOnlyMode;
    });
  }),
  on(ReservationActions.setReservationTempId, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.tmpId = Date.now().toString();
      }
    });
  }),
  on(ReservationActions.setIsSubmiting, (state, action) => {
    return produce(state, (draftState) => {
      draftState.isSubmiting = action.isSubmiting;
    });
  }),
  on(ReservationActions.setReservationRoomNumber, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.roomNumber = action.roomNumber;
      }
    });
  }),
  on(ReservationActions.setReservationRoomBookingId, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.roomBookingId = action.roomBookingId;
      }
    });
  }),
  on(ReservationActions.setReservationMetadata, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.reservation != null) {
        draftState.reservation.reservationDetails.metadata = action.metadata;
      }
    });
  }),
  on(ReservationActions.setPaymentLinkExpirationLocal, (state, action) => {
  return produce(state, (draftState) => {
    draftState.paymentLinkExpiresAt = action.paymentLinkExpiresAt;
  });
}),
on(ReservationActions.setPaymentLinkExpirationDirty, (state, action) => {
  return produce(state, (draftState) => {
    draftState.paymentLinkExpiresDirty = action.dirty;
  });
})
);
